import * as React from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { cn } from "@/lib/utils";

interface MultiSelectProps {
  options: { label: string; value: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  allowCustom?: boolean;
}

export function MultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "Select items...",
  className,
  allowCustom = false,
}: MultiSelectProps) {
  // Ensure value is always an array of strings
  const safeValue = React.useMemo(() => {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((item) => typeof item === "string" && item.length > 0);
  }, [value]);

  // Ensure options is always a valid array
  const safeOptions = React.useMemo(() => {
    if (!Array.isArray(options)) {
      return [];
    }
    return options.filter(
      (option) =>
        option &&
        typeof option.value === "string" &&
        typeof option.label === "string" &&
        option.value.length > 0 &&
        option.label.length > 0
    );
  }, [options]);

  // Convert string array to react-select format
  const selectedOptions = React.useMemo(() => {
    return safeValue.map((val) => {
      const existingOption = safeOptions.find((opt) => opt.value === val);
      return existingOption || { label: val, value: val };
    });
  }, [safeValue, safeOptions]);

  const handleChange = (selectedOptions: any) => {
    if (!onChange) return;
    const newValue = selectedOptions
      ? selectedOptions.map((option: any) => option.value)
      : [];
    onChange(newValue);
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: "40px",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#3b82f6" : "#9ca3af",
      },
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: "#f3f4f6",
      borderRadius: "6px",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "#374151",
      fontSize: "14px",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: "#6b7280",
      "&:hover": {
        backgroundColor: "#ef4444",
        color: "white",
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#9ca3af",
    }),
  };

  const SelectComponent = allowCustom ? CreatableSelect : Select;

  return (
    <div className={cn("w-full", className)}>
      <SelectComponent
        isMulti
        options={safeOptions}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        styles={customStyles}
        classNamePrefix="react-select"
        isClearable={false}
        isSearchable={true}
        formatCreateLabel={(inputValue: string) => `Add "${inputValue}"`}
        noOptionsMessage={() => "No options found"}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{
          DropdownIndicator: ({ innerProps, isDisabled }) => (
            <div
              {...innerProps}
              className={`px-2 ${isDisabled ? "opacity-50" : ""}`}
            >
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          ),
        }}
      />
    </div>
  );
}
